<!-- NavigationBar.vue -->
<template>
  <div class="nav-layout">
    <!-- Fixed top navbar - Simplified to just have logo and toggle -->
    <nav class="navbar">
      <div class="navbar-container">
        <!-- Left section: Logo/Toggle -->
        <div class="navbar-section">
          <button class="icon-button" @click="toggleSidebar">
            <i class="ic ic-menu ic-2x"></i>
          </button>
          <router-link to="/" class="navbar-logo">
            <img src="@/assets/images/sgLogo.png" alt="logo" />
          </router-link>
        </div>

        <!-- Right section: Auth -->
        <div class="navbar-section">
          <div class="auth-controls">
            <router-link v-if="!hasUser" to="/auth/login" class="auth-button">
              Sign In <i class="ic ic-login ic-2x"></i>
            </router-link>
            <a v-else @click="handleLogout" class="auth-button">
              <i class="ic ic-logout ic-2x"></i>
            </a>
          </div>
        </div>
      </div>
    </nav>

    <!-- Main content container -->
    <div class="main-content-container">
      <!-- Fixed sidebar -->
      <aside :class="['sidebar', { 'sidebar-open': isSidebarOpen }]">
        <div class="sidebar-inner">
          <h3 v-if="hasUser && userClaims?.name" class="user-greeting">Hi, {{userClaims?.name}}</h3>
          
          <div class="sidebar-menu">
            <!-- Primary navigation items -->
            <div class="menu-section">
              <div v-for="(route, index) in accessibleRoutes" :key="index" class="menu-item-wrapper">
                <!-- Regular menu item -->
                <router-link
                  v-if="route.type === 'router-link'"
                  :to="route.route" 
                  class="menu-item" 
                  @click="closeSidebar">
                  <i :class="[route.icon, 'ic-lg']"></i>
                  <span class="menu-text">{{ route.name }}</span>
                </router-link>
                
                <!-- Expandable parent menu item -->
                <div v-else-if="route.type === 'parent'" :class="['menu-parent', { 'menu-parent-expanded': activeItem === route.name }]">
                  <div class="menu-item" @click="toggleMenuItem(route.name)">
                    <i :class="[route.icon, 'ic-lg']"></i>
                    <span class="menu-text">{{ route.name }}</span>
                  </div>
                  
                  <!-- Child items -->
                  <div v-if="activeItem === route.name" class="menu-children">
                    <router-link 
                      v-for="(child, childIndex) in route.children" 
                      :key="childIndex"
                      :to="child.route" 
                      class="menu-item menu-child" 
                      @click="closeSidebar">
                      <span class="menu-text">{{ child.name }}</span>
                    </router-link>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Theme toggle and Auth actions in sidebar footer -->
            <div class="sidebar-footer">
              <!-- Theme toggle button -->
              <button 
                class="menu-item theme-toggle-sidebar" 
                @click="toggleTheme" 
                :title="isDarkTheme ? 'Switch to light mode' : 'Switch to dark mode'"
              >
                <!-- Sun icon (shown in dark mode) -->
                <svg 
                  v-if="isDarkTheme" 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  stroke-width="2" 
                  stroke-linecap="round" 
                  stroke-linejoin="round"
                  class="theme-icon"
                >
                  <circle cx="12" cy="12" r="5"></circle>
                  <line x1="12" y1="1" x2="12" y2="3"></line>
                  <line x1="12" y1="21" x2="12" y2="23"></line>
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                  <line x1="1" y1="12" x2="3" y2="12"></line>
                  <line x1="21" y1="12" x2="23" y2="12"></line>
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                  <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
                </svg>
                
                <!-- Moon icon (shown in light mode) -->
                <svg 
                  v-else 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  stroke-width="2" 
                  stroke-linecap="round" 
                  stroke-linejoin="round"
                  class="theme-icon"
                >
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
                </svg>
                <span class="menu-text">{{ isDarkTheme ? 'Light Mode' : 'Dark Mode' }}</span>
              </button>
              
              <!-- Logout button -->
              <a v-if="hasUser" @click="handleLogout" class="menu-item">
                <i class="ic ic-logout ic-lg"></i>
                <span class="menu-text">Logout</span>
              </a>
            </div>
          </div>
        </div>
      </aside>
      
      <!-- Main content area -->
      <main class="main-content">
        <slot></slot>
      </main>
    </div>

    <!-- Sidebar overlay (mobile only) -->
    <div 
      v-if="isSidebarOpen" 
      class="sidebar-overlay" 
      @click="closeSidebar"
    ></div>
  </div>
</template>

<script lang="js">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { auth } from '@/firebase';
import { useStore } from 'vuex';

export default {
  name: 'NavigationBar',
  props: {
    currentUser: {
      type: Object,
      default: null
    },
    userClaims: {
      type: Object,
      default: null
    },
    theme: {
      type: String,
      default: 'light'
    }
  },
  emits: ['toggle-theme'],
  setup(props, { emit }) {
    // Route and router
    const route = useRoute();
    const router = useRouter();
    const store = useStore();

    // Reactive state
    const activeItem = ref(null);
    const isSidebarOpen = ref(false);
    const isMobile = ref(false);
    const hasUser = ref(false);

    // Theme
    const isDarkTheme = computed(() => props.theme === 'dark');

    // Computed properties
    const isDefaultRoute = computed(() => !route.meta?.authRequired);

    // Update user state based on props and claims
    async function updateUserState() {
      hasUser.value = !!props.currentUser || (auth && !!auth.currentUser);
    }

    // Get accessible routes based on user permissions
    const accessibleRoutes = computed(() => {
      updateUserState();
      const currentClaims = props.userClaims || {};
      const currentPath = route.path;
      
      // Filter routes based on access rights
      let filtered = router.options.routes.filter(route => {
        if (!route.nav) return false;

        // Handle quote builder path
        if (route.path === '/quotebuilder' && auth.currentUser && currentClaims.company_id) {
          route.path = `/quotebuilder?c=${currentClaims.company_id}&u=${auth.currentUser.uid}`;
        }

        // Public routes logic
        if (!route.meta?.authRequired) {
          if (isDefaultRoute.value && isMobile.value) return true;
          if (route.nav.always_show_public_route) return true;
          return !hasUser.value;
        }
        
        // Auth required routes
        if (!auth.currentUser && route.meta?.authRequired) return false;
        
        // Routes requiring auth but no specific claims
        if (route.meta.authRequired && (!route.meta.claims || route.meta.claims.length === 0)) {
          return true;
        }
        
        // Routes requiring specific claims
        if (route.meta.claims && route.meta.claims.length > 0) {
          return route.meta.claims.some(claimsSet =>
            claimsSet.every(claim => 
              currentClaims.hasOwnProperty(claim) && currentClaims[claim] != null
            )
          );
        }
        
        return false;
      });

      // Process routes for sidebar display
      let result = [];
      let parents = {};
      
      for (let route of filtered) {
        let path = route.path;
        
        // Skip routes not matching company type
        if (route.meta?.companyType && route.meta.companyType !== currentClaims.company_type) continue;
        
        // Handle claim matching in routes
        if (route.meta?.matchClaim && (currentClaims[route.meta.matchClaim] || route.nav.default_param)) {
          if (!currentClaims[route.meta.matchClaim]) {
            if (route.nav.default_param) {
              currentClaims[route.meta.matchClaim] = route.nav.default_param;
            } else {
              continue;
            }
          }
          path = path.replace(`:${route.meta.matchClaim}`, currentClaims[route.meta.matchClaim]);
        }
        
        // Skip current path and dynamic paths
        if (path === currentPath) continue;
        if (path.indexOf(":") > -1) continue;
        
        // Create nav route object
        let navRoute = {
          route: path,
          name: route.nav.title,
          type: 'router-link',
          icon: route.nav.icon,
          children: [],
        };
        
        // Group by parent or add directly
        if (route.nav.parent) {
          if (!parents[route.nav.parent]) {
            parents[route.nav.parent] = {
              name: route.nav.parent,
              type: 'parent',
              icon: route.nav.icon,
              children: []
            };
          }
          parents[route.nav.parent].children.push(navRoute);
        } else {
          result.push(navRoute);
        }
      }
      
      // Handle parent routes
      if (Object.keys(parents).length === 1) {
        result = [...result, ...parents[Object.keys(parents)[0]].children];
      } else {
        for (let parent in parents) {
          result.push(parents[parent]);
        }
      }
      
      return result;
    });

    // Toggle theme
    function toggleTheme() {
      emit('toggle-theme');
    }

    // Methods
    function toggleMenuItem(itemName) {
      if (activeItem.value === itemName) {
        activeItem.value = null;
      } else {
        activeItem.value = itemName;
      }
    }

    function toggleSidebar() {
      isSidebarOpen.value = !isSidebarOpen.value;
      
      // When opening sidebar on mobile, prevent body scrolling
      if (isMobile.value) {
        document.body.style.overflow = isSidebarOpen.value ? 'hidden' : '';
      }
    }

    function closeSidebar() {
      isSidebarOpen.value = false;
      document.body.style.overflow = '';
    }

    function handleLogout() {
      localStorage.clear();
      window.location.href = '/auth/logout';
    }

    // Check screen size
    function checkScreenSize() {
      const wasAlreadyMobile = isMobile.value;
      isMobile.value = window.innerWidth < 992;
      
      // Layout adjustments when switching between mobile and desktop
      if (wasAlreadyMobile !== isMobile.value) {
        if (!isMobile.value && isSidebarOpen.value) {
          closeSidebar();
        }
      }
    }

    // Add watchers for props and state changes
    watch(() => props.currentUser, (newUser) => {
      updateUserState();
    }, { immediate: true });

    // Also watch for auth state changes
    watch(() => auth.currentUser, (newAuthUser) => {
      updateUserState();
    }, { immediate: true });

    watch(() => props.userClaims, (newClaims) => {
      updateUserState();
    }, { immediate: true, deep: true });

    // Lifecycle hooks
    onMounted(() => {
      updateUserState();
      checkScreenSize();
      window.addEventListener('resize', checkScreenSize);
      
      // Close sidebar when route changes
      const unsubscribe = router.afterEach(() => {
        if (isMobile.value && isSidebarOpen.value) {
          closeSidebar();
        }
      });
      
      // Clean up on unmount
      onBeforeUnmount(() => {
        window.removeEventListener('resize', checkScreenSize);
        unsubscribe();
        document.body.style.overflow = '';
      });
    });

    return {
      activeItem,
      isSidebarOpen,
      isMobile,
      hasUser,
      accessibleRoutes,
      isDefaultRoute,
      isDarkTheme,
      toggleMenuItem,
      toggleSidebar,
      closeSidebar,
      toggleTheme,
      handleLogout
    };
  }
}
</script>

<style scoped>
/* Layout components */
.nav-layout {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  overflow-x: hidden;
}

/* Top navigation bar */
.navbar {
  background-color: var(--bg-card);
  box-shadow: var(--shadow);
  height: var(--nav-height, 60px);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
}

.navbar-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 1.5rem;
  height: 100%;
  max-width: 1200px;
  margin: 0 auto;
}

.navbar-section {
  display: flex;
  align-items: center;
  gap: 1.25rem; /* Increased spacing between nav items */
}

.navbar-logo {
  margin-left: 1.25rem;
}

.navbar-logo img {
  height: 40px;
}

.icon-button {
  background: none;
  border: none;
  padding: 0.75rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-primary);
  transition: color 0.2s, transform 0.2s;
  font-size: 1.25rem;
}

.icon-button:hover {
  color: var(--primary-color);
  transform: scale(1.1);
}

.auth-button {
  display: flex;
  align-items: center;
  text-decoration: none;
  padding: 0.75rem 1.25rem;
  color: var(--text-primary);
  transition: color 0.2s;
  gap: 0.75rem; /* Space between text and icon */
  font-size: 1.25rem;
}

.auth-button:hover {
  color: var(--primary-color);
}

/* Content container */
.main-content-container {
  display: flex;
  margin-top: var(--nav-height, 60px);
  flex: 1;
  position: relative;
}

/* Sidebar */
.sidebar {
  position: fixed;
  top: calc(var(--nav-height, 60px) + 1rem);
  left: 1rem;
  width: var(--sidebar-width, 280px);
  max-height: calc(100vh - var(--nav-height, 60px) - 2rem);
  background-color: var(--bg-card);
  border-right: 1px solid var(--border-color);
  z-index: 90;
  transform: translateX(calc(-100% - 1rem));
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow-y: auto;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border-radius: 0.5rem;
  max-width: calc(100% - 2rem);
}

.sidebar-open {
  transform: translateX(0);
}

.sidebar-inner {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.user-greeting {
  padding: 0;
  margin-bottom: 1.5rem;
  font-size: 1.2rem;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 1rem;
}

.sidebar-menu {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 0.75rem; /* Increased spacing between menu items */
}

.menu-section {
  margin-bottom: 1rem;
}

/* Menu items */
.menu-item-wrapper {
  margin-bottom: 0.5rem; /* Increased spacing between menu items */
}

.menu-item {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  text-decoration: none;
  color: var(--text-primary);
  transition: all 0.2s ease;
  border-radius: 0.5rem;
  gap: 0.75rem; /* Space between icon and text */
}

.menu-item:hover {
  background-color: var(--bg-hover);
  transform: translateX(4px);
}

.menu-item i {
  font-size: 1.5rem; /* Increased icon size */
  opacity: 0.8;
  min-width: 1.5rem; /* Ensure consistent width */
  display: flex;
  justify-content: center;
}

.menu-text {
  white-space: nowrap;
  font-size: 1rem;
}

/* Parent menu items */
.menu-parent-expanded > .menu-item {
  background-color: var(--bg-active);
  font-weight: 500;
}

/* Child menu items */
.menu-children {
  background-color: var(--bg-hover);
  border-radius: 0.5rem;
  margin: 0.25rem 0;
  padding: 0.25rem;
}

.menu-child {
  padding-left: 2.5rem;
  font-size: 0.9rem;
}

/* Theme toggle in sidebar */
.theme-toggle-sidebar {
  border: none;
  background: none;
  cursor: pointer;
  width: 100%;
  text-align: left;
}

.theme-icon {
  width: 1.5rem; /* Larger theme icon */
  height: 1.5rem;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  min-width: 1.5rem; /* Ensure consistent width */
  display: inline-block;
}

/* Sidebar footer with theme toggle and logout */
.sidebar-footer {
  margin-top: auto;
  padding-top: 1rem;
  border-top: 1px solid var(--border-color);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

/* Main content area */
.main-content {
  flex: 1;
  padding: 1.5rem;
  width: 100%;
}

/* Sidebar overlay */
.sidebar-overlay {
  position: fixed;
  top: var(--nav-height, 60px);
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--modal-overlay);
  z-index: 85;
}

/* Theme-specific styles */
.theme-glass .navbar {
  background-color: rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
}

.theme-glass .sidebar {
  background-color: rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-right: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.theme-glass .menu-item:hover {
  background-color: rgba(255, 255, 255, 0.3);
}

.theme-glass .menu-parent-expanded > .menu-item {
  background-color: rgba(255, 255, 255, 0.4);
}

.theme-glass .menu-children {
  background-color: rgba(255, 255, 255, 0.2);
}

/* Desktop styles */
@media (min-width: 992px) {
  .main-content {
    margin-left: 0;
    transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  .sidebar-open + .main-content {
    margin-left: var(--sidebar-width, 280px);
  }
}
</style>